import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { daIso, GIORNI_BREVI, messaggioErrore, quantiTesto } from '../../utils/agenda';
import Icona from '../cliente/Icone';
import { Iniziale, usePersone } from './Persone';
import Eliminazione from './Eliminazione';

// Lista dei clienti dello staff (Oggi, /admin/clienti): filtri per fase e Fermi, ricerca, "+ Nuovo cliente";
// righe con iniziale, nome e riassunto, fase a 5 tacche, ultima novità (rossa oltre 14 giorni), prossimo passo,
// chi segue, Apri. Ordinata per urgenza dal backend (backend/oggi.py).

const FASI = [['', 'Tutti'], ['ricerca', 'Ricerca'], ['visita', 'Visite'], ['proposta', 'Proposta'], ['mutuo', 'Mutuo'], ['rogito', 'Rogito']];

export const quandoNovita = (ultima) => {
  if (!ultima || ultima.giorni == null) return 'mai';
  if (ultima.giorni === 0) return 'Oggi';
  if (ultima.giorni === 1) return 'Ieri';
  return `${ultima.giorni} giorni fa`;
};

export const quandoProssimo = (p, oggi) => {
  if (!p) return '';
  const quando = p.data === oggi ? 'oggi' : `${GIORNI_BREVI[daIso(p.data).getDay()]} ${daIso(p.data).getDate()}`;
  return [quando + (p.ora ? ` ${p.ora}` : ''), p.chi].filter(Boolean).join(' · ');
};

// La scheda cliente la aprono admin e collaboratori (il collaboratore solo i suoi clienti: lo controlla il backend)
export const linkCliente = (id) => `/admin/clienti/${id}`;

const NuovoCliente = ({ chiudi, onCreato }) => {
  const [dati, setDati] = useState({ nome: '', cognome: '', telefono: '', email: '', citta: '', budget_max: '' });
  const [attesa, setAttesa] = useState(false);
  const [creato, setCreato] = useState(null);
  const campo = (k) => ({ value: dati[k], onChange: (e) => setDati({ ...dati, [k]: e.target.value }) });
  const invia = async (e) => {
    e.preventDefault();
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/staff/clienti`, { ...dati, citta_ricerca: dati.citta.split(',').map((c) => c.trim()).filter(Boolean) });
      setCreato(r.data);
      if (onCreato) onCreato(r.data);
    } catch (err) { toast.error(messaggioErrore(err)); } finally { setAttesa(false); }
  };
  return (
    <div className="card hero" style={{ marginBottom: '1rem' }} data-testid="nuovo-cliente">
      <div className="ttl"><div className="i"><Icona nome="persone" /></div><h2>Nuovo cliente</h2><button type="button" className="x" onClick={chiudi} aria-label="Chiudi">✕</button></div>
      {creato ? (
        <div>
          <p>Fatto. Date al cliente questo codice: lo scrive quando si registra e ritrova tutto quello che avete già inserito.</p>
          <p style={{ fontFamily: 'var(--serif)', fontSize: '2rem', color: 'var(--brass2)', letterSpacing: '.12em', margin: '.6rem 0' }}>{creato.codice_collegamento}</p>
          <div style={{ display: 'flex', gap: '.5rem' }}><button type="button" className="btn sm" onClick={chiudi}>Chiudi</button></div>
        </div>
      ) : (
        <form onSubmit={invia} className="follow" style={{ marginTop: 0 }}>
          <input {...campo('nome')} placeholder="Nome" aria-label="Nome" required />
          <input {...campo('cognome')} placeholder="Cognome" aria-label="Cognome" required />
          <input {...campo('telefono')} placeholder="Telefono" aria-label="Telefono" />
          <input {...campo('email')} placeholder="Email (facoltativa)" aria-label="Email" type="email" />
          <input {...campo('citta')} placeholder="Dove cerca (comuni, con la virgola)" aria-label="Dove cerca" />
          <input {...campo('budget_max')} placeholder="Budget massimo" aria-label="Budget massimo" inputMode="numeric" />
          <button type="submit" className="btn sm p" disabled={attesa}>Crea e dammi il codice</button>
        </form>
      )}
    </div>
  );
};

const ListaClienti = ({ limite = null, titolo = 'Clienti', oggi, aggiorna = 0, fermiIniziale = false, faseIniziale = '' }) => {
  const { userType } = useAuth();
  const navigate = useNavigate();
  const [fase, setFase] = useState(faseIniziale);
  const [fermi, setFermi] = useState(fermiIniziale);
  const [cerca, setCerca] = useState('');
  const [dati, setDati] = useState(null);
  const [nuovo, setNuovo] = useState(false);
  const [giro, setGiro] = useState(0);
  const [segue, setSegue] = useState('');
  const [elimina, setElimina] = useState(null);
  const { persone } = usePersone();

  // Se il server non risponde la lista lo dice ("Riprovate"): prima mostrava una lista vuota, "Nessun cliente qui"
  // e i totali a 0, che è un'altra cosa.
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/clienti-lista`, { params: { fase: fase || undefined, fermi: fermi || undefined, q: cerca || undefined, persona: segue || undefined } })
        .then((r) => { setErrore(false); setDati(r.data); }).catch(() => { setDati(null); setErrore(true); });
    }, cerca ? 200 : 0);
    return () => clearTimeout(t);
  }, [fase, fermi, cerca, aggiorna, giro, segue]);

  const righe = dati?.righe || [];
  const mostrate = limite ? righe.slice(0, limite) : righe;
  const t = dati?.totali || {};
  const conta = (n, una, tante) => (errore ? '—' : n == null ? '…' : quantiTesto(n, una, tante));
  const oggiIso = oggi || new Date().toISOString().slice(0, 10);
  return (
    <section data-testid="lista-clienti">
      <div className="cl-head r">
        {/* singolare e plurale: prima "1 attivi · 1 fermi" */}
        <h2>{titolo} <small>{conta(t.attivi, 'attivo', 'attivi')} · {conta(t.fermi, 'fermo', 'fermi')} · {conta(t.trattativa, 'in trattativa', 'in trattativa')}</small></h2>
        <div className="cl-tools">
          {FASI.map(([v, nome]) => (
            <button type="button" key={v || 'tutti'} className={`chip${fase === v && !fermi ? ' on' : ''}`} onClick={() => { setFase(v); setFermi(false); }}>{nome}</button>
          ))}
          <button type="button" className={`chip rosso${fermi ? ' on' : ''}`} onClick={() => { setFermi(!fermi); setFase(''); }}>Fermi</button>
          {persone.length > 1 && (
            <span className={`chip${segue ? ' on' : ''}`}>
              <select value={segue} onChange={(e) => setSegue(e.target.value)} aria-label="Chi segue" style={{ appearance: 'none', background: 'transparent', border: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>
                <option value="">Segue: tutti ▾</option>
                {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </span>
          )}
          <label className="srch"><Icona nome="cerca" /><input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca cliente…" aria-label="Cerca cliente" /></label>
          <button type="button" className="btn sm p" onClick={() => setNuovo(true)}>+ Nuovo cliente</button>
        </div>
      </div>
      {nuovo && <NuovoCliente chiudi={() => setNuovo(false)} onCreato={() => setGiro((g) => g + 1)} />}
      <div className="tbl r">
        <div className="tr testa"><div>Cliente</div><div>Fase</div><div>Ultima novità</div><div>Prossimo passo</div><div>Segue</div><div /></div>
        {!dati && !errore && <div className="tr tutti"><span className="muto">Carico i clienti…</span></div>}
        {errore && (
          <div className="tr tutti" data-testid="clienti-errore">
            <span className="muto" role="alert">Non riesco a caricare i clienti.{' '}
              <button type="button" onClick={() => setGiro((g) => g + 1)}>Riprovate</button>
            </span>
          </div>
        )}
        {dati && !righe.length && <div className="tr tutti"><span className="muto">Nessun cliente {fermi ? 'fermo' : cerca ? `per “${cerca}”` : 'qui'}.</span></div>}
        {mostrate.map((c) => (
          <div className="tr" key={c.id} data-testid={`cliente-${c.id}`}>
            <div className="nm"><div className="av">{c.iniziale}</div><div style={{ minWidth: 0 }}><b>{c.nome}</b><span>{c.riassunto}</span></div></div>
            <div className="ph"><i>{[1, 2, 3, 4, 5].map((n) => <u key={n} className={n <= c.fase.numero ? 'd' : undefined} />)}</i>{c.fase.nome}</div>
            <div className={`lt${c.fermo ? ' bad' : ''}`}>{quandoNovita(c.ultima)}<span>{c.ultima?.testo || 'nessuna novità'}</span></div>
            <div className="nx">{c.prossimo ? c.prossimo.titolo : <span className="muto">—</span>}{c.prossimo && <span>{quandoProssimo(c.prossimo, oggiIso)}</span>}</div>
            <div className="cs">{c.segue && <><Iniziale persona={c.segue} taglia={22} />{c.segue.nome}</>}</div>
            <div className="az">
              <Link className="btn sm" to={linkCliente(c.id, userType)}>Apri</Link>
              {userType === 'admin' && (
                <button type="button" className="btn sm rosso-vuoto ico-sola" onClick={() => setElimina(c.id)} aria-label={`Elimina ${c.nome}`} title="Elimina il cliente">
                  <Icona nome="cestino" /></button>
              )}
            </div>
          </div>
        ))}
        {limite && righe.length > limite && (
          <div className="tr tutti"><button type="button" onClick={() => navigate('/admin/clienti')}>Mostra tutti i {righe.length} clienti →</button></div>
        )}
      </div>
      {elimina && <Eliminazione tipo="cliente" id={elimina} onChiudi={() => setElimina(null)} onEliminato={() => { setElimina(null); setGiro((g) => g + 1); }} />}
    </section>
  );
};

export default ListaClienti;
